/*
 *  This file recruitment-module-independent.service.ts is part of HROne Inbox. The intellectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  Copyright Year: 2025
 *  (C) 2015-2025. All Rights reserved. Uneecops Workplace Solution PVT. LTD.
 */

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { shareReplay, catchError } from 'rxjs/operators';
import { HttpService } from './http-service';
import { defaultViewAccessIds, TGeneralSettingAPIResponse, TRecruiterAccessSettingAPIResponse, TProviderDetailAPIResponse, TCandidateProfileResponse } from '../models/recruitment-module-independent.model';
import { HroneGlobalStateService } from 'src/app/store/global/global-state.service';
import { TFilter } from 'src/app/modules/recruitment/utilities/recruitmentAPI.model';

@Injectable({
  providedIn: 'root'
})
export class RecruitmentModuleIndependentService {

  private cacheRecruitmentModuleIndependentService = new Map<string, Observable<any>>();
  recruiterAccessSetting!: Record<number, boolean>;

  private readonly apiUrls = {
    generalSettingList: () => `/api/recruitment/setting/GeneralSetting`,
    providerDetail: () => `/api/recruitment/setting/ProviderDetail`,
    candidateProfile: (candidateId: number) => `/api/recruitment/Candidate/${candidateId}`,
    talentPoolFilters: () => `/api/recruitment/Candidate/FiltersNew/0/0/true`
  };

  constructor(private http: HttpService,
    private globalStateService: HroneGlobalStateService
  ) { }

  public getChangeInRecruitmentSetting() {
    this.globalStateService.getRecruitmentGeneralSettingList().subscribe((settings: TGeneralSettingAPIResponse[]) => {
      if (settings.find(e => e.settingId === 44)?.status == 'Y') {
        this.setRecruitmentModuleIndependentService();
      }
      this.updateLocalCache(settings);
    });
  }

  private fetchDataForArray<T>(url: string): Observable<T[]> {
    if (!this.cacheRecruitmentModuleIndependentService.has(url)) {
      const response$: Observable<T[]> = this.http.Get(url).pipe(
        shareReplay(1)
      );
      this.cacheRecruitmentModuleIndependentService.set(url, response$);
    }
    return this.cacheRecruitmentModuleIndependentService.get(url) as Observable<T[]>;
  }

  resetCache(): void {
    this.cacheRecruitmentModuleIndependentService = new Map<string, Observable<any>>();
  }

  setRecruitmentModuleIndependentService(): void {
    this.globalStateService.getRecruiterAccessSetting().subscribe((settings: TRecruiterAccessSettingAPIResponse[]) => {
      if (!settings?.length) return;
      this.recruiterAccessSetting = settings.reduce((acc, setting) => {
        acc[setting.recruiterAccessMasterId] = defaultViewAccessIds.includes(setting.recruiterAccessMasterId)
          ? setting.isView
          : setting.isAction;
        return acc;
      }, {} as Record<number, boolean>);
    });
  }

  getRecruitmentGeneralSettingList(): Observable<TGeneralSettingAPIResponse[]> {
    return this.globalStateService.getRecruitmentGeneralSettingList();
  }

  getProviderDetails(): Observable<TProviderDetailAPIResponse[]> {
    return this.http.Get(this.apiUrls.providerDetail());
  }

  getCandidateProfile(candidateId: number): Observable<TCandidateProfileResponse | null> {
    if (!candidateId) {
      console.warn('getCandidateProfile: candidateId is required');
      return of(null);
    }
    const url = this.apiUrls.candidateProfile(candidateId);
    return this.http.Get(url).pipe(
      catchError((error) => {
        console.warn('Could not fetch candidate profile:', error);
        return of(null);
      })
    );
  }

  private updateLocalCache(data: TGeneralSettingAPIResponse[]): void {
    const url = this.apiUrls.generalSettingList();
    const response$ = of(data).pipe(shareReplay(1));
    this.cacheRecruitmentModuleIndependentService.set(url, response$);
  }

  getTalentPoolFilters(): Observable<TFilter[]> {
    const url = this.apiUrls.talentPoolFilters();
    return this.fetchDataForArray<TFilter>(url);
  }

}